import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, Shield } from 'lucide-react';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';

const Section = ({ id, title, children }) => (
  <section id={id} className="scroll-mt-24">
    <h2 className="font-mono-display text-xl text-white mb-3">{title}</h2>
    <div className="text-sm text-zinc-300/90 leading-relaxed space-y-3">{children}</div>
  </section>
);

const LAST_UPDATED = 'May 17, 2026';

export default function TermsOfService() {
  const { isAuthed } = useAuth();

  return (
    <Layout>
      <section className="max-w-4xl mx-auto px-6 py-10 sm:py-12">
        <div className="mb-8">
          <Link to="/" className="btn-ghost inline-flex mb-6">
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to home
          </Link>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-cyan-400/40 bg-cyan-400/5 text-cyan-200 text-xs uppercase tracking-widest mb-4">
            <FileText className="w-3.5 h-3.5" />
            Legal // terms
          </div>
          <h1 className="font-mono-display text-3xl sm:text-4xl text-white mb-3">
            Terms of <span className="neon-orange">Service</span>
          </h1>
          <p className="text-zinc-400 text-sm sm:text-base max-w-2xl leading-relaxed">
            These terms govern your access to and use of ValoRex. By creating an account or using the platform, you agree to them.
          </p>
          <p className="text-xs uppercase tracking-widest text-zinc-500 mt-4">
            Last updated: <span className="text-cyan-300/80">{LAST_UPDATED}</span>
          </p>
        </div>

        <div className="neon-card rounded-lg p-6 sm:p-8 mb-6 flex items-start gap-4 border-cyan-400/20">
          <div className="w-10 h-10 shrink-0 grid place-items-center rounded border border-cyan-400/40 bg-cyan-400/10">
            <Shield className="w-5 h-5 text-cyan-300" />
          </div>
          <p className="text-sm text-zinc-300 leading-relaxed">
            ValoRex is an independent account management and showcase tool. We are{' '}
            <span className="text-white">not affiliated with, endorsed by, or sponsored by Riot Games, Inc.</span>{' '}
            Valorant and all related marks are trademarks of Riot Games, Inc.
          </p>
        </div>

        <div className="neon-card rounded-lg p-6 sm:p-10 bracket-corners space-y-10">
          <Section id="acceptance" title="1. Acceptance of terms">
            <p>
              By accessing or using valorex.space or any related ValoRex application, services, or features (collectively, the “Service”), you confirm that you have read, understood, and agree to be legally bound by these Terms of Service (“Terms”) and any additional policies referenced herein.
              </p>
              <p>
              If you do not agree to these Terms, you must immediately discontinue use of the Service.
              These Terms constitute a legally binding agreement between you (“User,” “you”) and ValoRex (“Company,” “we,” “us,” “our”).
            </p>
          </Section>

          <Section id="service" title="2. Description of the service">
            <p>
              ValoRex is an independent third-party platform that provides tools for:
              <ul>
              <li>&#8226; Linking and managing Valorant accounts via authorized authentication flows </li>
              <li>&#8226; Viewing in-game statistics, inventory, and account-related data</li>
              <li>&#8226; Managing multiple linked accounts under subscription-based plans</li>
              <li>&#8226; Generating and sharing public profile pages</li>
              </ul>
              We reserve the right to modify, suspend, or discontinue any feature at any time without notice.
            </p>
          </Section>

          <Section id="eligibility" title="3. Eligibility">
            <p>
              You may use the Service only if you:
            <ul>
              <li>&#8226; Are at least 13 years of age (or the minimum legal age in your jurisdiction)</li>
              <li>&#8226; Have the legal capacity to enter into a binding agreement</li>
              <li>&#8226; Comply with all applicable local, national, and international laws</li>
            </ul>
              If you are under the required age or legally restricted, you are prohibited from using the Service.
            </p>
          </Section>

          <Section id="accounts" title="4. Accounts and security">
            <p>
              You must provide accurate registration information and keep your credentials confidential. You are responsible for all activity under
              your account. Notify us promptly if you suspect unauthorized access.
            </p>
            <p>
              You may only link Valorant accounts that you own or are explicitly authorized to manage. Do not share login credentials for ValoRex
              with others except as needed for your own operational security practices.
            </p>
          </Section>

          <Section id="riot" title="5. Third-Party Dependencies">
            <p>
              The Service depends on third-party systems, including authentication providers and external data sources.
            </p>
             <p> You acknowledge and agree that:
                <ul>
                <li>&#8226; We do not control third-party services</li>
                <li>&#8226; We are not responsible for downtime, data loss, or service interruptions caused by third parties</li>
                <li>&#8226; Data accuracy, availability, and completeness are not guaranteed</li>
                <li>&#8226; Third-party services may change or discontinue without notice</li>
                </ul>
                Your use of third-party services is governed solely by their own terms and policies.
            </p>
          </Section>

          <Section id="Account and Security" title="6. Account and Security">
            <p>
              You are solely responsible for maintaining the confidentiality of your account credentials.
            </p>
            <p> 
              You agree that:
              <ul>
                <li>&#8226; All activity under your account is your responsibility</li>
                <li>&#8226; You will notify us immediately of unauthorized access</li>
                <li>&#8226; You will not share your account with unauthorized individuals</li>
                <li>&#8226; You will only link accounts that you own or are authorized to manage</li>
              </ul>
            </p>
            <p>
              We are not liable for any loss or damage arising from unauthorized account use. Paid plans (Immortal, Radiant, and any future tiers) are billed manually in PKR. You submit payment proof; an administrator verifies
              the transaction before activation. We do not process card payments through an automated gateway on this platform.
            </p>

          </Section>

          <Section id="responsibilities" title="7. User Responsibilities">
            <p>
              You agree that you will:
            <ul>
              <li>&#8226; Provide accurate and up-to-date information</li>
              <li>&#8226; Use the Service only for lawful purposes</li>
              <li>&#8226; Comply with all applicable laws and regulations</li>
              <li>&#8226; Ensure your use does not violate third-party rights or platform policies</li>
            </ul>
            </p>
            <p>
                You are fully responsible for any content or actions performed through your account.
            </p>
          
          </Section>

          <Section id="acceptable-use" title="8. Acceptable Use Policy">
            <p>You agree NOT to:
            <ul>
              <li>&#8226; Violate any applicable law or regulation</li>
              <li>&#8226; Engage in fraud, abuse, harassment, or harmful activity</li>
              <li>&#8226; Attempt to gain unauthorized access to systems or accounts</li>
              <li>&#8226; Scrape, reverse engineer, or exploit the Service without written permission</li>
              <li>&#8226; Disrupt, overload, or interfere with system functionality</li>
              <li>&#8226; Introduce malware, viruses, or harmful code</li>
              <li>&#8226; Resell, sublicense, or commercially exploit the Service</li>
              <li>&#8226; Misrepresent identity or account ownership</li>
              <li>&#8226; Use automated tools (bots, scrapers) without authorization</li>
            </ul> </p>
            <p>We reserve the right to investigate and take legal or technical action against violations.</p>
          </Section>

          <Section id="content-and-data-usage" title="9. Content and Data Usage">
            <p>
              By using the Service, you may submit or generate content including account data, profiles, and shared links.
            </p>
            <p>
              You retain ownership of your content. However, you grant ValoRex a worldwide, non-exclusive, royalty-free license to:
            <ul>
              <li>&#8226; Host and store your data</li>
              <li>&#8226; Process and display content within the Service</li>
              <li>&#8226; Generate shareable public profile pages</li>
              <li>&#8226; Maintain system functionality and backups</li>
            </ul>
            </p>
            <p> 
              We may remove or restrict content at our sole discretion.
            </p>
          </Section>

          <Section id="sharing" title="10. Public Sharing">
            <p>
              Public profile links may expose selected account information.
            </p>
            <p>
              You acknowledge that:
            <ul>
                <li>&#8226; Shared links are accessible by anyone with the URL</li>
                <li>&#8226; We are not responsible for misuse of shared links</li>
                <li>&#8226; You are solely responsible for what you choose to publish</li>
                <li>&#8226; You must not publish private or third-party data without consent</li>
            </ul>
            </p>
          </Section>

          <Section id="SBP" title="11. Subscriptions, Billing, and Payments">
            <p>
              ValoRex offers paid subscription plans including but not limited to Immortal and Radiant tiers.
            </p>
            <p>
              Key terms:
              <ul>
                <li>&#8226;Payments are manually processed in PKR</li>
                <li>&#8226;Users must submit valid proof of payment</li>
                <li>&#8226;Activation occurs only after administrative verification</li>
                <li>&#8226;We reserve the right to refuse or delay activation</li>
                <li>&#8226;Pricing, features, and limits may change at any time</li>
              </ul>
              </p>
              <p>
              We are not responsible for incorrect payments, fraud, or third-party payment disputes.
              Refunds are not guaranteed and are issued solely at our discretion unless required by applicable law.
            </p>
          </Section>

          <Section id="service-avail" title="12. Service Availability">
            <p>
              We do not guarantee uninterrupted or error-free operation of the Service.
            </p>
            <p>
              We may:
              <ul>
                <li>&#8226; Modify or discontinue features at any time </li>
                <li>&#8226; Restrict access to parts of the Service</li>
                <li>&#8226; Perform maintenance without prior notice</li>
                <li>&#8226; Experience downtime due to external dependencies</li>
              </ul>
            </p>
            <p>We are not liable for any losses resulting from service interruptions.</p>
          </Section>

          <Section id="IP" title="13. Intellectual Property">
            <p>
              All content, design, branding, software, and functionality of ValoRex (excluding user-generated content and third-party assets) are the exclusive property of ValoRex.
            </p>
            <p>
              You may not:
              <ul>
                <li>&#8226; Copy, reproduce, or distribute any part of the Service</li>
                <li>&#8226; Modify or create derivative works</li>
                <li>&#8226; Use our branding or assets without permission</li>
              </ul>
            </p>
          </Section>

          <Section id="Termination" title="14. Termination">
            <p>
              We reserve the right to suspend or terminate your access to the Service at any time, with or without notice, if:
            </p>
            <p>
              <ul>
                <li>&#8226; You violate these Terms</li>
                <li>&#8226; You pose a security or legal risk</li>
                <li>&#8226; We are required to do so by law or third parties</li>
                <li>&#8226; We decide to discontinue the Service</li>
              </ul>
            </p>
            <p>
              Upon termination, your right to use the Service immediately ceases.
            </p>
          </Section>


          <Section id="DOW" title="15. Disclaimer of Warranties">
            <p>
              THE SERVICE IS PROVIDED ON AN “AS IS” AND “AS AVAILABLE” BASIS. TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES, INCLUDING BUT NOT LIMITED TO:
            </p>
            <p>
              <ul>
                <li>&#8226; Accuracy or reliability of data</li>
                <li>&#8226; Availability or uptime of the Service</li>
                <li>&#8226; Fitness for a particular purpose</li>
                <li>&#8226; Non-infringement</li>
                <li>&#8226; Error-free or uninterrupted operation</li>
              </ul>
              </p>
              <p> You use the Service entirely at your own risk. </p>
            
          </Section>

          <Section id="LoL" title="16. Limitation of Liability">
            <p>
              ValoRex and its operators shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to:
            </p>
            <p>
              <ul>
                <li>&#8226; Loss of data</li>
                <li>&#8226; Loss of accounts</li>
                <li>&#8226; Loss of profits or revenue</li>
                <li>&#8226; Service interruption</li>
                <li>&#8226; Unauthorized access</li>
                <li>&#8226; Third-party actions or failures</li>
              </ul>
              </p>
              <p> In all cases, our total liability shall not exceed the greater of: PKR 9000, OR The total amount you paid for the Service in the past 3 months </p>
            
          </Section>


          <Section id="Indemnification" title="17. Indemnification">
            <p>
              You agree to defend, indemnify, and hold harmless ValoRex, its operators, affiliates, and partners from any claims, damages, losses, or expenses arising from:
            </p>
            <p>
              <ul>
                <li>&#8226; Your use of the Service</li>
                <li>&#8226; Your violation of these Terms</li>
                <li>&#8226; Your violation of any law or third-party rights</li>
              </ul>
              </p>
            
          </Section>

          
          <Section id="LoL" title="18. Changes to Terms">
            <p>
              We reserve the right to modify these Terms at any time.
              When changes are made:
            </p>
            <p>
              <ul>
                <li>&#8226; The “Last Updated” date will be revised</li>
                <li>&#8226; Continued use of the Service constitutes acceptance of updated Terms</li>
                <li>&#8226; Significant changes may be communicated through the Service</li>
              </ul>
              </p>
              <p> In all cases, our total liability shall not exceed the greater of: PKR 9000, OR The total amount you paid for the Service in the past 3 months </p>
            
          </Section>

          
          <Section id="Gov-Law" title="19. Governing Law">
            <p>
              These Terms shall be governed and interpreted in accordance with the laws of Pakistan, without regard to conflict of law principles.
            </p>
            <p>
              Any disputes shall be subject to the exclusive jurisdiction of courts in Pakistan.
              </p>
            
          </Section>

          
          <Section id="Sev" title="20. Severability">
            <p>
              If any provision of these Terms is found invalid or unenforceable, the remaining provisions will continue in full force and effect.
            </p>
            
          </Section>

          
          <Section id="EA" title="21. Entire Agreement">
            <p>
              These Terms, along with any policies referenced herein, constitute the entire agreement between you and ValoRex and supersede any prior agreements or understandings.
            </p>
            
          </Section>

          
          <Section id="contact" title="22. Contact">
            <p>
              For questions regarding these Terms, contact us through the official support channels listed on valorex.space.
            </p>
            
          </Section>

        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link to={isAuthed ? '/dashboard' : '/auth/register'} className="btn-neon">
            {isAuthed ? 'Dashboard' : 'Get Started'}
          </Link>
          <Link to="/pricing" className="btn-ghost">
            View pricing
          </Link>
        </div>
      </section>
    </Layout>
  );
}
